import clsx from 'clsx'
import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react'
import { useClickAway } from 'react-use'

interface DropdownProps {
  className?: string
  direction?: 'left' | 'right'
  isOpen?: boolean
  renderToggle: FC
  setIsOpen?(o: boolean): void
  size?: 'medium' | 'large' | 'small'
}

export function LegacyDropdown({
  children,
  isOpen: isOpenProp,
  setIsOpen: setIsOpenProp,
  renderToggle: Toggle,
  direction = 'right',
  className = '',
  size = 'medium',
}: PropsWithChildren<DropdownProps>) {
  const [isOpen, setIsOpen] = useState(!!isOpenProp)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useClickAway(dropdownRef, () => setIsOpen(false))

  function handleClickToggle() {
    setIsOpen((prev) => !prev)
  }

  useEffect(() => {
    if (setIsOpenProp) setIsOpenProp(isOpen)
  }, [isOpen, setIsOpenProp])

  useEffect(() => {
    if (typeof isOpenProp === 'boolean') setIsOpen(isOpenProp)
  }, [isOpenProp, setIsOpen])

  return (
    <div className={clsx('relative', className)} ref={dropdownRef}>
      <span className="cursor-pointer" onClick={handleClickToggle} onKeyDown={handleClickToggle} role="none">
        <Toggle />
      </span>

      <DropdownMenu
        direction={direction}
        isOpen={isOpen}
        role="menu"
        size={size}
        style={{ boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.12)', maxHeight: '50vh', overflow: 'auto' }}
      >
        {children}
      </DropdownMenu>
    </div>
  )
}

interface DropdownMenuProps {
  direction: 'left' | 'right'
  isOpen: boolean
  size: string
}

const DropdownMenu = ({
  direction,
  isOpen,
  size,
  className,
  children,
  ...props
}: DropdownMenuProps & React.ComponentPropsWithoutRef<'div'>) => (
  <div
    {...props}
    className={clsx(
      'bg-primary absolute z-20 top-full w-52 rounded-2xl transform translate-y-0',
      size === 'medium' ? 'w-52' : size === 'small' ? 'w-44' : 'w-72',
      isOpen ? 'visible' : 'invisible',
      direction === 'right' ? 'left-0' : 'right-0',
      size === 'small' ? 'rounded' : 'rounded-2xl',
      '[&>*]:w-full [&>*]:text-left [&>*]:font-medium',
      size === 'small' ? '[&>*]:p-3' : '[&>*]:last-of-type:pb-6 [&>*]:first:pt-6 [&>*]:px-6 [&>*]:py-3',
      className,
    )}
  >
    {children}
  </div>
)
